import { useEffect, useRef, useState, type HTMLAttributes } from 'react';
import { LIMITS, displayTag, isFace, isSeed, type Member, type RoomId } from '@engineer-os/chat';
import { RoomPicker } from './RoomPicker';
import { RoomStrip } from './RoomStrip';
import { MemberList } from './MemberList';
import { MessageList, type OpenMenu } from './MessageList';
import { PinnedStrip } from './PinnedStrip';
import { SearchBar, SearchResults } from './SearchBar';
import { Inbox } from './Inbox';
import { ReportDialog } from './ReportDialog';
import { DeleteDialog } from './DeleteDialog';
import type { AttachmentApi } from '../../state/useAttachment';
import { Composer } from './Composer';
import { NamePrompt } from './NamePrompt';
import { SignInPrompt } from './SignInPrompt';
import { FacePicker } from './FacePicker';
import { Avatar, VerifiedMark } from './Identity';
import { ProfileCard } from './ProfileCard';
import { Teams } from './Teams';
import { Support } from './Support';
import { chatLink, whoIs } from '../../lib/chat';
import { teamForRoom } from '../../lib/teams';
import { useStrings } from '../../state/useLocale';
import { Machine } from '../../ui';
import type { ToastFn } from '../../App';
import type { ChatApi, ChatRow } from '../../state/useChat';
import type { AccountApi } from '../../state/useAccount';
import { useTeams } from '../../state/useTeams';
import { useSupport } from '../../state/useSupport';
import type { PublicMission } from '../../types';

/**
 * The inside of the panel: a header, the conversation, the composer and a
 * footer that says how the line is.
 *
 * Composition and one piece of policy only. The policy is that at most one
 * popover is open at a time — rooms, members, the mentions, the faces at the
 * bottom, or one message's menu — because two overlapping panels in 360px is a
 * mess, and the component that owns both ends of the panel is the one place
 * that can promise it. That is why a message's menu is state *here* rather
 * than inside the row that draws it: a menu is a popover like the others, and
 * a list that owned its own would be a second authority on how many may be
 * open. One value, so two cannot be.
 *
 * The search field is the exception, and deliberately. It is not a popover —
 * it overlays nothing — and it holds a draft, so shutting it because somebody
 * looked at the member list would throw away something they typed. It lives in
 * its own boolean and closes when it is closed.
 *
 * A learner with no name yet is *listening*: connected, reading, not in the
 * room. So the prompt takes the composer's place rather than the whole panel —
 * the conversation is right there above it, which is the only honest argument
 * for joining it. Changing a name later is the one that takes the panel over:
 * by then the room is already yours, and the form is a deliberate stop rather
 * than an invitation. Reporting a message takes it over for the same reason.
 *
 * Which prompt goes in that slot is the whole of the post gate, on this side.
 * Where signing in is possible, posting is for signed-in people and the slot
 * holds SignInPrompt; where it is not — a dev server with no `.dev.vars`, a
 * deployment that never configured Google — there is no gate and the slot
 * holds NamePrompt, as it always did. `account.enabled` is the same question
 * the room asks itself in `gated()`, asked of the same configuration, so the
 * two ends cannot disagree about which door they are showing.
 *
 * ## Two rooms
 *
 * Everything on this screen is about `chat.active` — one of the one or two
 * rooms the tab is holding. The header's room name is the active room's, the
 * conversation is the active room's, and so are its pins, its members and its
 * unread. The other room is one button away in the strip and costs nothing to
 * return to, which is the entire point: walking into a level used to evict you
 * from the conversation you were in, and now it does not.
 */

/**
 * What may be open, of the things that may only be open one at a time.
 *
 * The named ones are the panel's own; an object is one message's menu,
 * carrying which message and whether it opened straight into the reaction
 * palette (the `+` at the end of a reaction row opens it that way, because a
 * reaction that costs two decisions is not the cheap act it is supposed to
 * be).
 */
type Popover = 'rooms' | 'members' | 'face' | 'inbox' | 'teams' | 'support' | OpenMenu | null;

const isMenu = (p: Popover): p is OpenMenu => typeof p === 'object' && p !== null;

export interface ChatPanelProps {
  chat: ChatApi;
  /** Whether signing in is possible, whether it is happening, and how to start
   *  it. `enabled` decides which prompt a listener is shown. */
  account: AccountApi;
  mission: PublicMission | null;
  draft: string;
  onDraft(text: string): void;
  /** The picture picked and not yet sent, held above the panel because the
   *  composer unmounts and an object URL has to be released exactly once. */
  attach: AttachmentApi;
  onClose(): void;
  /** For the one thing a message action has to say out loud: a copied link. */
  notify: ToastFn;
  /** The pointer handlers that move the panel. The header is the handle, and
   *  ChatDock owns the arithmetic; this spreads them onto it. */
  grip: Pick<HTMLAttributes<HTMLElement>, 'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel'>;
  /**
   * Somebody outside the dock asked for the support desk, this many times
   * (lib/dock.ts, `openSupport`).
   *
   * A count rather than a flag, because each press is a separate request: a
   * reader who opens the desk, closes the popover and presses the same line
   * again means it both times, and a boolean that was already true would sit
   * there doing nothing. Zero is "nobody has asked", which is every ordinary
   * visit.
   */
  showSupport: number;
}

export function ChatPanel({ chat, account, mission, draft, onDraft, attach, onClose, notify, grip, showSupport }: ChatPanelProps) {
  const s = useStrings().chat;
  const [renaming, setRenaming] = useState(false);
  const [popover, setPopover] = useState<Popover>(null);
  const [searching, setSearching] = useState(false);
  const [replyTo, setReplyTo] = useState<ChatRow | null>(null);
  const [reporting, setReporting] = useState<ChatRow | null>(null);
  const [deleting, setDeleting] = useState<ChatRow | null>(null);
  /**
   * The message the composer is rewriting, and what was in the composer before
   * it started.
   *
   * Both here, in one piece of state, because they are one fact: the field is
   * on loan, and giving it back means knowing what to give back. Kept in the
   * panel rather than in the composer for the reason the draft itself is kept
   * above the panel — a component that owns a rescue value and then unmounts
   * has lost the thing it was rescuing, and the composer unmounts whenever a
   * listener arrives, a room is switched, or a dialog takes the panel over.
   *
   * `was` is the draft as it stood, which is very often the empty string. It
   * is stored anyway rather than treated as "nothing to restore": somebody who
   * had half a sentence typed and then fixed a typo in an older message should
   * find the half sentence where they left it, and that is exactly the case
   * where losing it is most annoying.
   */
  const [editing, setEditing] = useState<{ row: ChatRow; was: string } | null>(null);
  /**
   * Whose card is open, held as the snapshot it was opened from rather than
   * as a tag.
   *
   * A tag alone would be enough only for somebody the room still has, and
   * most of the names on screen belong to people who have closed the tab — a
   * room keeps a thousand messages and the member list holds whoever is
   * connected this second. So what is kept is everything the click knew, and
   * `whoIs` prefers the live member row over it on every render: open the
   * card on somebody who is here and it keeps up with them as they move
   * through a level, open it on somebody who is gone and it stays the record
   * it was, and says so.
   */
  const [viewing, setViewing] = useState<Member | null>(null);
  // A team is an account's, so this asks nothing at all while signed out.
  const teams = useTeams(chat.verified);
  // A support thread is an account's too, and the same is true of it. Unlike
  // teams this one polls, because somebody is waiting on an answer to it —
  // the argument is in state/useSupport.ts.
  const support = useSupport(chat.verified);
  /**
   * Somebody has answered and the answer has not been read.
   *
   * Two places need it and they need to agree: the mark drawn on the header
   * button, and the sentence that button gives its name to. It is `state` as
   * well as `lastFrom` because a resolved thread whose last word was the
   * desk's is not news — it is how every conversation here ends.
   */
  const replyWaiting = support.thread !== null && support.thread.state === 'open' && support.thread.lastFrom === 'staff';

  /**
   * The desk, opened from outside the dock.
   *
   * The offer block's line for a reader the card rail will not serve is the
   * only caller, and it reaches here through `lib/dock.ts` rather than through
   * a prop, because the block is on a page that is this panel's sibling at
   * best and on another screen entirely at worst.
   *
   * It opens the popover whatever the header says. The button up there is
   * drawn only for a signed-in reader with a desk to talk to, and this arrives
   * from somebody who may be neither — which is fine, and deliberate: every
   * state `Support` can be in has a sentence, signed out and closed included,
   * and being told "sign in and we can start" is a better answer than a
   * control that was not there.
   *
   * `refresh` because the snapshot may be minutes old or never taken, and what
   * this reader is about to decide — whether starting a conversation is worth
   * it — rests on how many people are actually at the desk.
   */
  useEffect(() => {
    // Falsy rather than `=== 0`: the tests mount this panel outside the
    // compiler's reach (web/tsconfig.json covers `src` only), and a missing
    // prop read as "not zero" would open the desk on every mount.
    if (!showSupport) return;
    setPopover('support');
    support.refresh();
    // `support` is a new object on every render; the ask is the only trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSupport]);

  const headRef = useRef<HTMLElement>(null);
  const footRef = useRef<HTMLElement>(null);

  const active = chat.active;

  // A click outside the bar that opened it shuts whichever popover is open.
  // A message's menu is not in either bar, so it gets the same test applied to
  // its own holder: anywhere inside the menu or its trigger is inside.
  useEffect(() => {
    if (!popover) return;
    const onDown = (e: MouseEvent) => {
      if (!(e.target instanceof Node)) return;
      const inside =
        headRef.current?.contains(e.target) ||
        footRef.current?.contains(e.target) ||
        (e.target instanceof Element && e.target.closest('.chat-menu-holder') !== null);
      if (!inside) setPopover(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [popover]);

  // Answering somebody in one room and then switching to the other would send
  // the reply into a room the message is not in, where the room would refuse
  // it as `bad-target`. The reply belongs to the conversation it was started
  // in, so it ends when that conversation leaves the screen.
  useEffect(() => {
    setReplyTo(null);
    setPopover((p) => (isMenu(p) ? null : p));
    // The card goes with it. It is a card about somebody in the room that was
    // on screen, and the actions on it — mentioning, above all — are about
    // the room the composer below is pointed at.
    setViewing(null);
  }, [active.room]);

  if (renaming) {
    return (
      <NamePrompt
        mode="change"
        room={active.room}
        // Only a named learner can be renaming: the footer's button is the
        // one way in, and a listener does not get one.
        initial={chat.name ?? ''}
        onSubmit={(name) => {
          chat.setName(name);
          setRenaming(false);
        }}
        onCancel={() => setRenaming(false)}
      />
    );
  }

  if (reporting) {
    return (
      <ReportDialog
        row={reporting}
        onSend={(reason) => chat.report(reporting.id, reason)}
        onClose={() => setReporting(null)}
      />
    );
  }

  if (deleting) {
    return <DeleteDialog row={deleting} onConfirm={() => chat.remove(deleting.id)} onClose={() => setDeleting(null)} />;
  }

  const here = active.members.length;
  // Reading without being in the room, decided the way the room decides it.
  // Gated, that is being signed in and nothing else: a browser that picked a
  // name back when it could still post has one in storage to this day, and
  // `chat.name` would happily report it — which would put a composer in front
  // of someone the server will refuse. Ungated, having a name is the whole of
  // it, as before.
  const listening = account.enabled ? !chat.verified : chat.name === null;
  // An account picture is neither one of the faces nor an operator, and that
  // is the whole test: whoever is wearing one cannot be handed the picker,
  // because it is Google's picture and not this browser's to swap. Building a
  // figure replaces it, which is what the link inside the picker is for.
  const wearsPicture = active.you !== null && active.you.avatar != null && !isFace(active.you.avatar) && !isSeed(active.you.avatar);
  // Acting in a room needs a socket that is in it. A listener may read, search
  // and follow a link, and that is all — the same line the server draws.
  const canPost = !listening && active.status === 'open' && active.you !== null;
  // Pinning is a verified member's. The room refuses anyone else, so the menu
  // says why rather than offering a button that will come back as an error.
  const canPin = canPost && active.you?.verified === true;
  const mentions = chat.inbox.mentions.length;
  const toggle = (which: Exclude<Popover, null>) => setPopover((p) => (p === which ? null : which));

  /**
   * What a room is called on screen.
   *
   * The protocol names the numbered and the level rooms and answers the
   * generic word for a team's, deliberately — a team is called whatever its
   * members called it, which lives in D1 and is not something a pure function
   * can know. This is the one place that holds both the roster and the
   * catalogue, so this is where the two are put together.
   */
  const roomName = (room: RoomId): string => teamForRoom(teams.teams, room)?.name ?? s.rooms.label(room);

  /** Open the card on somebody, from a member row or from a name on a message. */
  const showWho = (m: Member) => {
    setViewing(m);
    // Opened from the member list as often as not, and that is a popover. A
    // takeover with a popover still open over it is the arrangement the
    // one-at-a-time rule exists to prevent.
    setPopover(null);
  };

  // Null here would be a tag that is in neither the room nor the snapshot it
  // was opened from, which this call cannot produce: the snapshot is always
  // about the tag it is keyed by. So the card is simply not drawn rather than
  // the state being corrected mid-render, and the panel falls back to the
  // conversation — which is the right answer to "who is this" going missing,
  // and costs nothing to be wrong about.
  const who = viewing && whoIs(viewing.tag, active.members, viewing);
  if (who) {
    return (
      <ProfileCard
        who={who}
        own={active.you !== null && active.you.tag === who.member.tag}
        blocked={chat.blocked.includes(who.member.tag)}
        canPost={canPost}
        // The record half needs a session, not a name: `chat.verified` is the
        // same question `/api/chat/profile` asks of the cookie, so an ungated
        // deployment's named-but-accountless reader is correctly told to sign
        // in rather than shown a section that will never fill.
        signedIn={chat.verified}
        // Only the teams this reader leads. A member's invite is refused by
        // the Worker, so offering one would be offering a press that comes
        // back as an error.
        leads={teams.teams.filter((t) => t.role === 'lead').map((t) => ({ id: t.id, name: t.name }))}
        onInvite={async (teamId) => {
          const done = await teams.invite(teamId, who.member.tag);
          // Null means it worked, and the card says so in its own words. A
          // refusal comes back as the Worker's reason, worded here, so the
          // card can say which thing went wrong rather than "that failed".
          if (done.ok) return null;
          switch (done.reason) {
            case 'team is full':
              return s.teams.teamFull;
            case 'already a member':
              return s.teams.alreadyIn;
            case 'already asked':
              return s.teams.alreadyAsked;
            case 'not a lead':
              return s.teams.notLead;
            default:
              return s.teams.failed;
          }
        }}
        onMention={() => {
          // Straight into the draft the composer is already holding, with one
          // space between it and whatever was typed before. The *name* is what
          // goes in, because the name is what the composer matches against the
          // member list to find the tag on the way out
          // (components/chat/mentions.ts) — putting a tag here would write a
          // line that looked like a mention and addressed nobody.
          const head = draft.length > 0 && !draft.endsWith(' ') ? `${draft} ` : draft;
          onDraft(`${head}@${who.member.name} `);
          setViewing(null);
        }}
        onBlock={(on) => chat.block(who.member.tag, on)}
        onClose={() => setViewing(null)}
      />
    );
  }

  /** Go to a message in the room on screen: a reply's parent, or a pin. */
  const goTo = (id: number) => {
    chat.goTo(active.room, id);
    setPopover(null);
  };

  return (
    <>
      <header ref={headRef} className="chat-head" {...grip}>
        {/* One room and this is the picker, exactly as it has always been.
            Two, and the other one is a tab beside it. */}
        <RoomStrip rooms={chat.rooms} active={active.room} onShow={chat.show} label={roomName}>
          <RoomPicker
            room={active.room}
            label={roomName}
            recent={chat.recent}
            mission={mission}
            following={chat.following}
            occupancy={chat.occupancy}
            onRefresh={chat.refreshOccupancy}
            open={popover === 'rooms'}
            onToggle={() => toggle('rooms')}
            onClose={() => setPopover(null)}
            onJoin={chat.join}
            onFollow={chat.followLevel}
          />
        </RoomStrip>

        <div className="chat-head-side">
          <SearchBar
            open={searching}
            busy={chat.searching?.loading === true}
            onOpen={() => {
              setSearching(true);
              setPopover(null);
            }}
            onClose={() => {
              setSearching(false);
              chat.clearSearch();
            }}
            onSearch={chat.search}
          />
          {/* Only when there is something in it, or somebody whose account is
              holding one. A listener is already being told what an account
              buys, in the composer's place; a second pitch in a 280px header
              is noise. */}
          {(chat.verified || mentions > 0) && (
            <button
              type="button"
              className="disclose chat-inbox-btn"
              aria-expanded={popover === 'inbox'}
              aria-controls="chat-inbox"
              title={s.inbox.title}
              onClick={() => toggle('inbox')}
            >
              <span className="label">{s.inbox.open(mentions)}</span>
            </button>
          )}
          {/* Only for somebody who has an account to hang a team on. A
              signed-out reader is already being told what an account buys, in
              the composer's place; a second pitch in a 280px header is noise —
              the same rule the mentions button follows. */}
          {chat.verified && (
            <button
              type="button"
              className="disclose chat-teams-btn"
              aria-expanded={popover === 'teams'}
              aria-controls="chat-teams"
              title={s.teams.title}
              onClick={() => {
                // Asked when somebody is actually looking, which is the only
                // moment an invitation is worth a request (state/useTeams.ts).
                if (popover !== 'teams') teams.refresh();
                toggle('teams');
              }}
            >
              <span className="label">{s.teams.open(teams.invites.length)}</span>
            </button>
          )}
          {/* Only where there is actually somebody to ask. `enabled` is false
              for a deployment with the feature off, with no accounts, or with
              an empty roster, and a door into silence is worse than no door —
              which is the same judgement `lib/support.ts` makes about the
              field. The thread keeps the button alive even if the roster
              empties afterwards, because a conversation already going has to
              stay reachable. */}
          {chat.verified && (support.enabled || support.thread !== null) && (
            <button
              type="button"
              className="disclose chat-support-btn"
              aria-expanded={popover === 'support'}
              aria-controls="chat-support"
              // The whole sentence, in both the places a sentence belongs: the
              // name a screen reader reads out, and the tooltip a pointer
              // uncovers. What is *drawn* is one word and a mark, because the
              // long form measures 217px in a bar that has 305 of a 360px
              // panel already spoken for, and a header that cannot fit its
              // close button is a header nobody can shut (chat.css).
              title={s.support.open(replyWaiting)}
              aria-label={s.support.open(replyWaiting)}
              onClick={() => {
                // Somebody looking is worth a request, the way the teams
                // button spends one on invitations — and here it is the
                // difference between an answer read now and an answer read
                // in under a minute.
                if (popover !== 'support') support.refresh();
                toggle('support');
              }}
            >
              <span className="label">{s.support.title}</span>
              {replyWaiting && <span className="chat-support-dot" aria-hidden="true" />}
            </button>
          )}
          <button
            type="button"
            className="disclose chat-here"
            aria-expanded={popover === 'members'}
            aria-controls="chat-members"
            title={s.panel.whoIsHere}
            onClick={() => toggle('members')}
          >
            <span className="label">{active.status === 'open' ? s.panel.here(here) : s.panel.hereUnknown}</span>
          </button>
        </div>

        {/* Outside the row it used to sit in, and that is the whole point.
            The controls beside it scroll when the panel is narrow enough that
            they cannot all be drawn (chat.css, `.chat-head-side`); the way out
            of the panel is the one thing that may never be the casualty of
            that, so it is a sibling of the scroller rather than its last
            child. Escape closes the panel too, but a pointer has to have
            something to press. */}
        <button type="button" className="chat-x" aria-label={s.panel.close} title={s.panel.closeWhy} onClick={onClose}>
          <svg viewBox="0 0 12 12" aria-hidden="true" width="10" height="10">
            <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </button>

        {popover === 'members' && (
          <div
            id="chat-members"
            className="chat-pop"
            onKeyDown={(e) => {
              if (e.key !== 'Escape') return;
              e.preventDefault();
              setPopover(null);
            }}
          >
            <MemberList members={active.members} you={active.you} onOpen={showWho} />
          </div>
        )}

        {popover === 'teams' && (
          <div
            id="chat-teams"
            className="chat-pop"
            onKeyDown={(e) => {
              if (e.key !== 'Escape') return;
              e.preventDefault();
              setPopover(null);
            }}
          >
            <Teams
              teams={teams}
              signedIn={chat.verified}
              active={active.room}
              onOpen={(room) => {
                // Opening a team's room is picking a room by hand, so it pins
                // the way typing a number does: walking into a level should
                // not evict somebody from the team room they just opened.
                chat.join(room);
                setPopover(null);
              }}
            />
          </div>
        )}

        {popover === 'support' && (
          <div
            id="chat-support"
            className="chat-pop"
            onKeyDown={(e) => {
              if (e.key !== 'Escape') return;
              e.preventDefault();
              setPopover(null);
            }}
          >
            <Support
              support={support}
              signedIn={chat.verified}
              active={active.room}
              onOpen={(room) => {
                // `join` and not `show`, for the reason a team's room is
                // joined: this pins it as home, so walking into the next
                // level does not evict somebody from a conversation they
                // are in the middle of having with a person.
                chat.join(room);
                setPopover(null);
              }}
            />
          </div>
        )}

        {popover === 'inbox' && (
          <div
            id="chat-inbox"
            className="chat-pop"
            onKeyDown={(e) => {
              if (e.key !== 'Escape') return;
              e.preventDefault();
              setPopover(null);
            }}
          >
            <Inbox
              inbox={chat.inbox}
              signedIn={chat.verified}
              onSeen={chat.markInboxSeen}
              notifyMentions={chat.notifyMentions}
              onNotify={chat.setNotifyMentions}
              onOpen={(room, id) => {
                chat.goTo(room, id);
                setPopover(null);
              }}
            />
          </div>
        )}
      </header>

      {/* What the room kept. Keyed on the room, so switching rooms hands back
          a collapsed bar rather than the other room's list, expanded. */}
      {!chat.searching && <PinnedStrip key={active.room} pins={active.pins} onOpen={goTo} />}

      {chat.searching ? (
        <SearchResults
          q={chat.searching.q}
          results={chat.searching.results}
          loading={chat.searching.loading}
          onOpen={(id) => {
            goTo(id);
            setSearching(false);
            chat.clearSearch();
          }}
          onBack={() => {
            setSearching(false);
            chat.clearSearch();
          }}
        />
      ) : (
        <MessageList
          rows={active.messages}
          you={active.you}
          members={active.members}
          status={active.status}
          hasEarlier={active.hasEarlier}
          loadingEarlier={active.loadingEarlier}
          onEarlier={chat.earlier}
          onRetry={chat.retry}
          blocked={chat.blocked}
          highlight={chat.highlight}
          canPost={canPost}
          canPin={canPin}
          pinsFull={active.pins.length >= LIMITS.pin}
          menu={isMenu(popover) ? popover : null}
          onMenu={(msg, reacting) => setPopover({ msg, reacting })}
          onMenuClose={() => setPopover(null)}
          onReact={chat.react}
          onReply={(row) => {
            setReplyTo(row);
            // A reply and an edit compete for the same field and are
            // contradictory intentions; starting either ends the other, and
            // the draft the edit borrowed goes back where it came from.
            if (editing) onDraft(editing.was);
            setEditing(null);
            setPopover(null);
          }}
          onPin={chat.pin}
          onEdit={(row) => {
            // The words as they stand now, into the field. `editing.was` keeps
            // whatever was being written so Cancel and Save can both put it
            // back; a second Edit while already editing abandons the first,
            // which is why `was` is read from the existing mode rather than
            // from the draft that is currently on screen — the draft is the
            // first message's text, and stashing that would put somebody
            // else's sentence into the composer on the way out.
            setEditing({ row, was: editing?.was ?? draft });
            onDraft(row.text);
            setReplyTo(null);
            setPopover(null);
          }}
          onDelete={(row) => {
            setDeleting(row);
            setPopover(null);
          }}
          onReport={(row) => {
            setReporting(row);
            setPopover(null);
          }}
          onBlock={chat.block}
          onWho={showWho}
          onGoTo={goTo}
          link={(id) => chatLink(active.room, id)}
          notify={notify}
        />
      )}

      {!account.loaded ? (
        /* Which door to offer is not known yet, and the wrong one is worse
           than none: a name prompt that becomes a sign-in prompt a moment
           later can be read, and typed into, before it changes. The slot
           stays empty for the one request it takes to find out. */
        <div className="chat-waiting" aria-hidden="true" />
      ) : listening ? (
        account.enabled ? (
          <SignInPrompt room={active.room} busy={account.busy} onSignIn={account.signIn} />
        ) : (
          <NamePrompt mode="join" room={active.room} inline onSubmit={chat.setName} />
        )
      ) : (
        /* `you` and not just an open socket: a listener that has this second
           picked a name is connected but not yet in the room, and the Send
           button should wait for the `you` that says it is. */
        <Composer
          value={draft}
          onChange={onDraft}
          ready={canPost}
          members={active.members}
          you={active.you}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          editing={editing?.row ?? null}
          onCancelEdit={() => {
            onDraft(editing?.was ?? '');
            setEditing(null);
          }}
          attach={attach}
          pictures={account.pictures}
          onSend={(text, opts) => {
            // The picture goes with the words, and only a picture the bucket
            // has: one still on its way up, or one that failed, is not
            // something to send — the composer keeps the Send button off
            // while either is true, and this is the second lock on the same
            // door, because the first one is a disabled attribute and a
            // disabled attribute is a suggestion.
            const image = attach.attachment?.image ?? undefined;
            const sent = chat.send(text, { ...opts, ...(image ? { image } : {}) });
            if (sent) {
              setReplyTo(null);
              attach.clear();
            }
            return sent;
          }}
          onSave={(text) => {
            if (!editing) return false;
            // The field goes back to what was in it before either way this
            // ends, and the mode ends only if the frame actually went. A
            // refusal — a socket that dropped between the menu and the button
            // — leaves the edit open with the new words still in it, which is
            // the one state where somebody can press Save again rather than
            // retype the sentence.
            if (!chat.edit(editing.row.id, text)) return false;
            onDraft(editing.was);
            setEditing(null);
            return true;
          }}
        />
      )}

      <footer ref={footRef} className="chat-foot">
        <span className={`chat-dot is-${active.status}`} aria-hidden="true" />
        {/* Your own face, and the way to another one. Disabled rather than
            hidden while an account picture is what the room is showing: the
            face is still there to see, it is simply not yours to swap. */}
        {active.you && (
          <button
            type="button"
            className="chat-face-btn"
            aria-expanded={popover === 'face'}
            aria-controls="chat-faces"
            aria-label={s.panel.changeFace}
            title={wearsPicture ? s.panel.accountPicture : s.panel.changeFace}
            disabled={wearsPicture}
            onClick={() => toggle('face')}
          >
            <Avatar src={active.you.avatar} tag={active.you.tag} size={18} />
          </button>
        )}
        {popover === 'face' && active.you && (
          <div
            id="chat-faces"
            className="chat-pop is-up"
            onKeyDown={(e) => {
              if (e.key !== 'Escape') return;
              e.preventDefault();
              setPopover(null);
            }}
          >
            <FacePicker
              current={active.you.avatar ?? null}
              tag={active.you.tag}
              onPick={(next) => {
                chat.setFace(next);
                setPopover(null);
              }}
            />
          </div>
        )}
        <span className="chat-status" role="status">
          {active.status === 'open' ? (
            listening ? (
              s.status.listening
            ) : (
              <>
                {/* A name is the person's, not the machine's, and it can be
                    written in either script. `auto` lets the first strong
                    character decide, and isolates it either way, so an Arabic
                    name reads right-to-left inside an English panel and the
                    tag beside it is never dragged along. */}
                <span dir="auto">{chat.name}</span>
                {active.you && <Machine className="chat-status-tag">#{displayTag(active.you.tag)}</Machine>}
                {active.you?.verified && <VerifiedMark />}
              </>
            )
          ) : active.status === 'connecting' ? (
            s.status.connecting
          ) : active.banned ? (
            /* Before the other two, because it is the only one of the three
               that is not going to fix itself. A banned socket reconnects
               into a door that is shut, and "offline — retrying" would be
               technically true and completely misleading. */
            s.status.banned(active.banned.reason)
          ) : active.full ? (
            s.status.roomFull
          ) : (
            s.status.offline
          )}
        </span>
        {/* A signed-in name is the account's, and there is nothing to change
            here; a listener has none yet, and the prompt above is already
            asking for it. */}
        {!chat.verified && !listening && (
          <button type="button" className="chat-link" onClick={() => setRenaming(true)}>
            {s.panel.changeName}
          </button>
        )}
      </footer>
    </>
  );
}

export default ChatPanel;
